// 期間・金額・都道府県での絞り込み
//
// 期間と都道府県は押した時点で反映する。金額だけは打っている途中に投げないよう、
// 確定（フォーカスが外れる / Enter）してから反映する。
import { X } from 'lucide-react'
import { useEffect, useState } from 'react'

import type { ReceiptPrefectureCount } from '@/api/types/receipt'
import { BottomSheet } from '@/components/ui/bottom-sheet'
import { Button } from '@/components/ui/button'
import { Checkbox } from '@/components/ui/checkbox'
import { Input } from '@/components/ui/input'
import { cn } from '@/lib/utils'

import { matchedPreset, PERIOD_PRESETS, periodRange, type ReceiptListFilters } from '../receipt-query'

type Props = {
  open: boolean
  onOpenChange: (open: boolean) => void
  filters: ReceiptListFilters
  onChange: (filters: ReceiptListFilters) => void
  /** 選択肢に出す都道府県。件数は都道府県以外の条件で数えたもの */
  prefectures: ReceiptPrefectureCount[]
}

const toAmount = (value: string): number | null => {
  const digits = value.replace(/[^\d]/g, '')
  if (!digits) return null
  return Number.parseInt(digits, 10)
}

export const ReceiptFilterSheet = ({ open, onOpenChange, filters, onChange, prefectures }: Props) => {
  const [minInput, setMinInput] = useState('')
  const [maxInput, setMaxInput] = useState('')

  // 外から条件が変わったとき（クリアなど）に入力欄も揃える
  useEffect(() => {
    setMinInput(filters.amountMin === null ? '' : String(filters.amountMin))
    setMaxInput(filters.amountMax === null ? '' : String(filters.amountMax))
  }, [filters.amountMin, filters.amountMax])

  const preset = matchedPreset(filters)

  const applyPreset = (value: (typeof PERIOD_PRESETS)[number]['value']) => {
    if (preset === value) {
      onChange({ ...filters, dateFrom: null, dateTo: null })
      return
    }
    const range = periodRange(value)
    onChange({ ...filters, dateFrom: range.from, dateTo: range.to })
  }

  const commitAmount = () => {
    const min = toAmount(minInput)
    const max = toAmount(maxInput)
    if (min === filters.amountMin && max === filters.amountMax) return
    onChange({ ...filters, amountMin: min, amountMax: max })
  }

  const togglePrefecture = (value: string) => {
    const next = filters.prefectures.includes(value)
      ? filters.prefectures.filter((item) => item !== value)
      : [...filters.prefectures, value]
    onChange({ ...filters, prefectures: next })
  }

  return (
    <BottomSheet title="絞り込み" open={open} onOpenChange={onOpenChange} snapPoints={[0.85]}>
      <div className="flex flex-col gap-6 px-4 pb-6">
        <section className="flex flex-col gap-2">
          <h3 className="text-xs text-muted-foreground">期間</h3>
          <div className="flex gap-1.5">
            {PERIOD_PRESETS.map((option) => (
              <Button
                key={option.value}
                variant={preset === option.value ? 'default' : 'outline'}
                size="sm"
                className="text-xs"
                onClick={() => applyPreset(option.value)}
              >
                {option.label}
              </Button>
            ))}
            {(filters.dateFrom || filters.dateTo) && (
              <Button
                variant="ghost"
                size="sm"
                className="ml-auto text-xs"
                onClick={() => onChange({ ...filters, dateFrom: null, dateTo: null })}
              >
                <X className="size-3.5" />
                解除
              </Button>
            )}
          </div>
          <div className="flex items-center gap-2">
            <Input
              type="date"
              aria-label="購入日の下限"
              value={filters.dateFrom ?? ''}
              onChange={(event) => onChange({ ...filters, dateFrom: event.target.value || null })}
            />
            <span className="text-muted-foreground">〜</span>
            <Input
              type="date"
              aria-label="購入日の上限"
              value={filters.dateTo ?? ''}
              onChange={(event) => onChange({ ...filters, dateTo: event.target.value || null })}
            />
          </div>
        </section>

        <section className="flex flex-col gap-2">
          <h3 className="text-xs text-muted-foreground">金額</h3>
          <div className="flex items-center gap-2">
            <Input
              type="text"
              inputMode="numeric"
              placeholder="下限"
              aria-label="金額の下限"
              value={minInput}
              onChange={(event) => setMinInput(event.target.value)}
              onBlur={commitAmount}
              onKeyDown={(event) => event.key === 'Enter' && commitAmount()}
            />
            <span className="text-muted-foreground">〜</span>
            <Input
              type="text"
              inputMode="numeric"
              placeholder="上限"
              aria-label="金額の上限"
              value={maxInput}
              onChange={(event) => setMaxInput(event.target.value)}
              onBlur={commitAmount}
              onKeyDown={(event) => event.key === 'Enter' && commitAmount()}
            />
          </div>
        </section>

        <section className="flex flex-col gap-2">
          <div className="flex items-center justify-between">
            <h3 className="text-xs text-muted-foreground">都道府県</h3>
            {filters.prefectures.length > 0 && (
              <Button
                variant="ghost"
                size="sm"
                className="text-xs"
                onClick={() => onChange({ ...filters, prefectures: [] })}
              >
                <X className="size-3.5" />
                解除
              </Button>
            )}
          </div>
          {prefectures.length === 0 ? (
            <p className="text-sm text-muted-foreground">場所が紐付いたレシートがありません。</p>
          ) : (
            <ul className="flex flex-col">
              {prefectures.map((item) => {
                const checked = filters.prefectures.includes(item.prefecture)
                return (
                  <li key={item.prefecture} className="flex items-center gap-3 border-b border-border/50 py-2.5">
                    <Checkbox
                      id={`pref-${item.prefecture}`}
                      checked={checked}
                      onCheckedChange={() => togglePrefecture(item.prefecture)}
                    />
                    <label
                      htmlFor={`pref-${item.prefecture}`}
                      className={cn(
                        'flex flex-1 cursor-pointer justify-between text-sm',
                        !checked && 'text-muted-foreground',
                      )}
                    >
                      {item.prefecture}
                      <span className="tabular-nums">{item.count}</span>
                    </label>
                  </li>
                )
              })}
            </ul>
          )}
        </section>
      </div>
    </BottomSheet>
  )
}
